import React, { useState, useEffect, useCallback } from 'react';
import {
    ClipboardList, Plus, X,
    Play, CheckCircle, XCircle, Trash2, Edit2, Users,
    Package, Layers, AlertTriangle, RefreshCw,
    ChevronDown, ChevronUp, ArrowUp, ArrowDown, Loader,
    UserCheck, Calendar, BarChart2, Eye,
} from 'lucide-react';
import { api } from '../../config/api';
import secureStorage from '../../utils/secureStorage';
import toast from 'react-hot-toast';
import {
    SearchInput, ToolbarSelect, ActiveFilters, ListLoader, ListEmpty, ListCard,
    thClass, mobileCardClass, mobileListClass, mobileRowClass, Avatar, RowActions, Pagination, Modal, ModalFooter,
} from '../../stock/StockUI';
import { inputClass } from '../../ClientsTable/clientFormUI';
import moment from 'moment';
import 'moment/locale/fr';
import AppleSelect from '../Common/AppleSelect';
import {tabClass, tabIconClass} from '../Common/TabBar';
import Button from '../Common/Button';

moment.locale('fr');

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Catalogue form field
const fieldClass = inputClass(false);

const getAccountHeaders = async () => {
    const account = await secureStorage.getItem('selectedAccount');
    return { 'X-account-id': account?.id };
};

// ─── Status configs ───────────────────────────────────────────────────────────

const SESSION_STATUS = {
    draft:       { label: 'Brouillon',    bg: 'bg-gray-100 dark:bg-gray-700',   text: 'text-gray-600 dark:text-gray-400',   dot: 'bg-gray-400 dark:bg-gray-500'   },
    in_progress: { label: 'En cours',     bg: 'bg-blue-100 dark:bg-blue-900/30',   text: 'text-blue-700 dark:text-blue-400',   dot: 'bg-blue-500'   },
    completed:   { label: 'Terminée',     bg: 'bg-amber-100 dark:bg-amber-900/30',  text: 'text-amber-700 dark:text-amber-400',  dot: 'bg-amber-500'  },
    validated:   { label: 'Validée',      bg: 'bg-green-100 dark:bg-green-900/30',  text: 'text-green-700 dark:text-green-400',  dot: 'bg-green-500'  },
    cancelled:   { label: 'Annulée',      bg: 'bg-red-100 dark:bg-red-900/30',    text: 'text-red-700 dark:text-red-400',    dot: 'bg-red-400'    },
};

const ASSIGNMENT_STATUS = {
    pending:     { label: 'En attente',   bg: 'bg-gray-100 dark:bg-gray-700',   text: 'text-gray-600 dark:text-gray-400'   },
    in_progress: { label: 'En cours',     bg: 'bg-blue-100 dark:bg-blue-900/30',   text: 'text-blue-700 dark:text-blue-400'   },
    submitted:   { label: 'Soumis',       bg: 'bg-amber-100 dark:bg-amber-900/30',  text: 'text-amber-700 dark:text-amber-400'  },
    validated:   { label: 'Validé',       bg: 'bg-green-100 dark:bg-green-900/30',  text: 'text-green-700 dark:text-green-400'  },
};

const SCOPE_LABELS = {
    all:      { label: 'Tout le catalogue', icon: Layers },
    category: { label: 'Par catégorie',     icon: Layers },
    product:  { label: 'Par produit',       icon: Package },
};

const COUNT_STATUS = {
    pending:   { label: 'En attente', bg: 'bg-gray-100 dark:bg-gray-700',   text: 'text-gray-600 dark:text-gray-400'  },
    counted:   { label: 'Compté',     bg: 'bg-blue-100 dark:bg-blue-900/30',   text: 'text-blue-700 dark:text-blue-400'  },
    adjusted:  { label: 'Ajusté',     bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400'},
    validated: { label: 'Validé',     bg: 'bg-green-100 dark:bg-green-900/30',  text: 'text-green-700 dark:text-green-400' },
};

// ─── StatusBadge ─────────────────────────────────────────────────────────────

const StatusBadge = ({ status, config }) => {
    const c = config[status] || { label: status, bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-600 dark:text-gray-400', dot: 'bg-gray-400 dark:bg-gray-500' };
    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}>
            {c.dot && <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`}/>}
            {c.label}
        </span>
    );
};

// ─── GapBadge ────────────────────────────────────────────────────────────────

const GapBadge = ({ gap }) => {
    if (gap === 0 || gap === null || gap === undefined) {
        return <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">±0</span>;
    }
    if (gap > 0) {
        return (
            <span className="inline-flex items-center gap-0.5 text-xs font-medium text-green-600 dark:text-green-400">
                <ArrowUp className="w-3 h-3"/>{'+' + gap}
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-0.5 text-xs font-medium text-red-600 dark:text-red-400">
            <ArrowDown className="w-3 h-3"/>{gap}
        </span>
    );
};

// ─── CreateSessionModal ───────────────────────────────────────────────────────

const CreateSessionModal = ({ session, onClose, onSave }) => {
    const [form, setForm] = useState({ name: session?.name || '', note: session?.note || '' });
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!form.name.trim()) { toast.error('Le nom est obligatoire'); return; }
        setSaving(true);
        try {
            const headers = await getAccountHeaders();
            if (session) {
                await api.patch(`/inventory/sessions/${session.id}`, form, { headers });
                toast.success('Session modifiée');
            } else {
                await api.post('/inventory/sessions', form, { headers });
                toast.success('Session créée');
            }
            onSave();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title={session ? 'Modifier la session' : 'Nouvelle session d\'inventaire'}
            subtitle={session ? 'Modifiez le nom ou la note' : 'Créez une session pour planifier un inventaire'}
            onClose={onClose}
            size="sm"
            footer={<ModalFooter onCancel={onClose} onConfirm={handleSave} saving={saving} label={session ? 'Enregistrer' : 'Créer'}/>}
        >
            <div className="space-y-4">
                <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Nom <span className="text-red-500 dark:text-red-400">*</span></label>
                    <input
                        type="text"
                        value={form.name}
                        onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                        placeholder="Ex: Inventaire mensuel Mai 2026"
                        className={fieldClass}
                    />
                </div>
                <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Note <span className="text-gray-400 dark:text-gray-500 font-normal">(optionnel)</span></label>
                    <textarea
                        value={form.note}
                        onChange={e => setForm(p => ({ ...p, note: e.target.value }))}
                        placeholder="Informations complémentaires..."
                        rows={3}
                        className={`${fieldClass} resize-none`}
                    />
                </div>
            </div>
        </Modal>
    );
};

// ─── AssignmentModal ──────────────────────────────────────────────────────────

const AssignmentModal = ({ sessionId, assignment, onClose, onSave }) => {
    const [form, setForm] = useState({
        employee_id: assignment?.employee?.id || '',
        scope_type: assignment?.scope_type || 'all',
        scope_ids: assignment?.scope_ids || [],
        note: assignment?.note || '',
    });
    const [employees, setEmployees] = useState([]);
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const headers = await getAccountHeaders();
                const [empRes, catRes] = await Promise.all([
                    api.get('/employees', { headers }),
                    api.get('/categories?page=1&itemsPerPage=200', { headers }),
                ]);
                setEmployees(empRes.data?.data || empRes.data || []);
                setCategories(catRes.data?.data || catRes.data || []);
            } catch { /* ignore */ }
            setLoading(false);
        };
        load();
    }, []);

    useEffect(() => {
        if (form.scope_type !== 'product') return;
        const load = async () => {
            try {
                const headers = await getAccountHeaders();
                const res = await api.get('/products?page=1&itemsPerPage=200', { headers });
                setProducts(res.data?.data || res.data || []);
            } catch { /* ignore */ }
        };
        load();
    }, [form.scope_type]);

    const toggleScopeId = (id) => {
        setForm(p => ({
            ...p,
            scope_ids: p.scope_ids.includes(id)
                ? p.scope_ids.filter(x => x !== id)
                : [...p.scope_ids, id],
        }));
    };

    const handleSave = async () => {
        if (!form.employee_id) { toast.error('Sélectionnez un employé'); return; }
        if (form.scope_type !== 'all' && form.scope_ids.length === 0) {
            toast.error('Sélectionnez au moins un élément pour le scope');
            return;
        }
        setSaving(true);
        try {
            const headers = await getAccountHeaders();
            const payload = {
                employee_id: form.employee_id,
                scope_type: form.scope_type,
                scope_ids: form.scope_type === 'all' ? [] : form.scope_ids,
                note: form.note,
            };
            if (assignment) {
                await api.patch(`/inventory/assignments/${assignment.id}`, {
                    scope_type: payload.scope_type,
                    scope_ids: payload.scope_ids,
                    note: payload.note,
                }, { headers });
                toast.success('Assignment modifié');
            } else {
                await api.post(`/inventory/sessions/${sessionId}/assignments`, payload, { headers });
                toast.success('Employé assigné');
            }
            onSave();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Erreur');
        } finally {
            setSaving(false);
        }
    };

    const scopeItems = form.scope_type === 'category' ? categories : products;
    const scopeLabel = form.scope_type === 'category' ? 'catégories' : 'produits';

    return (
        <Modal
            title={assignment ? 'Modifier l\'assignment' : 'Assigner un employé'}
            subtitle="Définissez la zone de comptage"
            onClose={onClose}
            size="md"
            footer={<ModalFooter onCancel={onClose} onConfirm={handleSave} saving={saving} label={assignment ? 'Enregistrer' : 'Assigner'}/>}
        >
            {loading ? (
                <ListLoader/>
            ) : (
                <div className="space-y-4">
                    {/* Employee select */}
                    {!assignment && (
                        <div>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                                Employé <span className="text-red-500 dark:text-red-400">*</span>
                            </label>
                            <AppleSelect
                                value={form.employee_id}
                                onChange={e => setForm(p => ({ ...p, employee_id: e.target.value }))}
                                className={fieldClass}
                            >
                                <option value="">Sélectionnez un employé...</option>
                                {employees.map(emp => (
                                    <option key={emp.id} value={emp.id}>
                                        {emp.firstName || emp.first_name || ''} {emp.lastName || emp.last_name || emp.name || ''}
                                    </option>
                                ))}
                            </AppleSelect>
                        </div>
                    )}

                    {/* Scope type */}
                    <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                            Zone de comptage <span className="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                            {[
                                { value: 'all', label: 'Tout', icon: Layers },
                                { value: 'category', label: 'Catégorie', icon: Layers },
                                { value: 'product', label: 'Produit', icon: Package },
                            ].map(({ value, label, icon: Icon }) => (
                                <button
                                    key={value}
                                    onClick={() => setForm(p => ({ ...p, scope_type: value, scope_ids: [] }))}
                                    type="button"
                                    className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border text-sm font-medium transition-all ${
                                        form.scope_type === value
                                            ? 'border-primary bg-primary/5 text-primary'
                                            : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:border-primary'
                                    }`}
                                >
                                    <Icon className="w-4 h-4"/>
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Scope IDs selection */}
                    {form.scope_type !== 'all' && (
                        <div>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                                Sélectionner les {scopeLabel} <span className="text-red-500 dark:text-red-400">*</span>
                                {form.scope_ids.length > 0 && (
                                    <span className="ml-2 text-primary font-normal">({form.scope_ids.length} sélectionné{form.scope_ids.length > 1 ? 's' : ''})</span>
                                )}
                            </label>
                            <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-48 overflow-y-auto">
                                {scopeItems.length === 0 ? (
                                    <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">Aucun élément disponible</p>
                                ) : (
                                    scopeItems.map(item => (
                                        <label key={item.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-0">
                                            <input
                                                type="checkbox"
                                                checked={form.scope_ids.includes(item.id)}
                                                onChange={() => toggleScopeId(item.id)}
                                                className="w-4 h-4 text-primary rounded border-gray-300 dark:border-gray-600"
                                            />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">{item.name}</span>
                                        </label>
                                    ))
                                )}
                            </div>
                        </div>
                    )}

                    {/* Note */}
                    <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                            Note <span className="text-gray-400 dark:text-gray-500 font-normal">(optionnel)</span>
                        </label>
                        <input
                            type="text"
                            value={form.note}
                            onChange={e => setForm(p => ({ ...p, note: e.target.value }))}
                            placeholder="Ex: Zone A — Rayon épicerie"
                            className={fieldClass}
                        />
                    </div>
                </div>
            )}
        </Modal>
    );
};

// ─── ValidateModal ────────────────────────────────────────────────────────────

const ValidateModal = ({ session, onClose, onValidated }) => {
    const [applyAdjustments, setApplyAdjustments] = useState(true);
    const [saving, setSaving] = useState(false);

    const handleValidate = async () => {
        setSaving(true);
        try {
            const headers = await getAccountHeaders();
            await api.post(
                `/inventory/sessions/${session.id}/validate`,
                { apply_stock_adjustments: applyAdjustments },
                { headers }
            );
            toast.success('Session validée avec succès');
            onValidated();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Erreur lors de la validation');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title="Valider l'inventaire"
            subtitle={`Session : ${session.name}`}
            onClose={onClose}
            size="sm"
            footer={<ModalFooter onCancel={onClose} onConfirm={handleValidate} saving={saving} label="Valider" tone="text-green-600 dark:text-green-400"/>}
        >
            <div className="space-y-4">
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 flex gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"/>
                    <p className="text-sm text-amber-700 dark:text-amber-400">
                        Cette action est irréversible. La session passera en statut <strong>Validée</strong>.
                    </p>
                </div>

                <div
                    onClick={() => setApplyAdjustments(p => !p)}
                    className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                        applyAdjustments ? 'border-primary bg-primary/5' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                    }`}
                >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        applyAdjustments ? 'border-primary bg-primary' : 'border-gray-300 dark:border-gray-600'
                    }`}>
                        {applyAdjustments && <CheckCircle className="w-3 h-3 text-white"/>}
                    </div>
                    <div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Appliquer les ajustements de stock</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            Crée des mouvements de stock pour chaque écart détecté
                        </p>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

// ─── ReportModal ──────────────────────────────────────────────────────────────

const ReportModal = ({ session, onClose }) => {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState({});

    useEffect(() => {
        const load = async () => {
            try {
                const headers = await getAccountHeaders();
                const res = await api.get(`/inventory/sessions/${session.id}/report`, { headers });
                setReport(res.data);
            } catch {
                toast.error('Erreur lors du chargement du rapport');
            }
            setLoading(false);
        };
        load();
    }, [session.id]);

    const toggleExpand = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

    return (
        <Modal
            title="Rapport d'écarts"
            subtitle={session.name}
            onClose={onClose}
            size="xl"
        >
            {loading ? (
                <ListLoader/>
            ) : !report ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">Rapport non disponible</p>
            ) : (
                <div className="space-y-5">
                    {/* Summary */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 border border-gray-200 dark:border-gray-700 rounded-lg divide-x divide-y sm:divide-y-0 divide-gray-200 dark:divide-gray-700 overflow-hidden">
                        {[
                            { label: 'Total articles', value: report.summary?.total || 0, color: 'text-gray-900 dark:text-gray-100' },
                            { label: 'Comptés', value: report.summary?.counted || 0, color: 'text-blue-600 dark:text-blue-400' },
                            { label: 'Écarts positifs', value: report.summary?.positive_gaps || 0, color: 'text-green-600 dark:text-green-400' },
                            { label: 'Écarts négatifs', value: report.summary?.negative_gaps || 0, color: 'text-red-600 dark:text-red-400' },
                        ].map(stat => (
                            <div key={stat.label} className="px-4 py-3">
                                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{stat.label}</p>
                                <p className={`text-xl font-bold ${stat.color}`}>{stat.value}</p>
                            </div>
                        ))}
                    </div>

                    {/* Per assignment */}
                    {report.assignments?.length > 0 && (
                        <div>
                            <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider mb-2">Détail par employé</h4>
                            <div className="space-y-2">
                                {report.assignments.map(asgn => (
                                    <div key={asgn.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                        <button
                                            onClick={() => toggleExpand(asgn.id)}
                                            className="w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition text-left"
                                        >
                                            <div className="flex items-center gap-3">
                                                <UserCheck className="w-4 h-4 text-gray-500 dark:text-gray-400"/>
                                                <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
                                                    {asgn.employee?.firstName || asgn.employee?.name || 'Employé'}
                                                    {asgn.employee?.lastName ? ` ${asgn.employee.lastName}` : ''}
                                                </span>
                                                <StatusBadge status={asgn.status} config={ASSIGNMENT_STATUS}/>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="text-xs text-gray-500 dark:text-gray-400">{asgn.counted || 0}/{asgn.total || 0} comptés</span>
                                                {expanded[asgn.id] ? <ChevronUp className="w-4 h-4 text-gray-400 dark:text-gray-500"/> : <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500"/>}
                                            </div>
                                        </button>
                                        {expanded[asgn.id] && asgn.items?.length > 0 && (
                                            <div className="divide-y divide-gray-100 dark:divide-gray-700">
                                                <div className="grid grid-cols-4 px-4 py-3 bg-gray-50 dark:bg-gray-700/50 text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                                                    <span className="col-span-2">Produit</span>
                                                    <span className="text-right">Attendu</span>
                                                    <span className="text-right">Écart</span>
                                                </div>
                                                {asgn.items.map((item, i) => (
                                                    <div key={i} className="grid grid-cols-4 px-4 py-2.5 text-sm items-center">
                                                        <span className="col-span-2 text-gray-700 dark:text-gray-300 truncate">
                                                            {item.product_name || item.product?.name}
                                                            {item.variant_name && <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">({item.variant_name})</span>}
                                                        </span>
                                                        <span className="text-right text-gray-600 dark:text-gray-400">{item.expected_qty}</span>
                                                        <span className="text-right"><GapBadge gap={item.gap}/></span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* All items with gaps */}
                    {report.items?.filter(i => i.gap !== 0).length > 0 && (
                        <div>
                            <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider mb-2">Articles avec écart</h4>
                            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                <div className="grid grid-cols-4 px-4 py-3 bg-gray-50 dark:bg-gray-700/50 text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                                    <span className="col-span-2">Produit</span>
                                    <span className="text-right">Attendu</span>
                                    <span className="text-right">Écart</span>
                                </div>
                                <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-64 overflow-y-auto">
                                    {report.items.filter(i => i.gap !== 0).map((item, i) => (
                                        <div key={i} className="grid grid-cols-4 px-4 py-2.5 text-sm items-center">
                                            <span className="col-span-2 text-gray-700 dark:text-gray-300 truncate">
                                                {item.product_name || item.product?.name}
                                                {item.variant_name && <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">({item.variant_name})</span>}
                                            </span>
                                            <span className="text-right text-gray-600 dark:text-gray-400">{item.expected_qty}</span>
                                            <span className="text-right"><GapBadge gap={item.gap}/></span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </Modal>
    );
};

// ─── CountAdjustModal ─────────────────────────────────────────────────────────

const CountAdjustModal = ({ count, mode, onClose, onSave }) => {
    const [form, setForm] = useState({
        adjusted_qty: count.counted_qty ?? count.expected_qty ?? 0,
        admin_note: '',
    });
    const [saving, setSaving] = useState(false);

    const isValidate = mode === 'validate';

    const handleSave = async () => {
        setSaving(true);
        try {
            const headers = await getAccountHeaders();
            if (isValidate) {
                await api.post(`/inventory/counts/${count.id}/validate`, { admin_note: form.admin_note }, { headers });
                toast.success('Comptage validé');
            } else {
                if (form.adjusted_qty === '' || form.adjusted_qty === null) {
                    toast.error('Saisissez une quantité ajustée');
                    setSaving(false);
                    return;
                }
                await api.patch(`/inventory/counts/${count.id}/adjust`, {
                    adjusted_qty: parseFloat(form.adjusted_qty),
                    admin_note: form.admin_note,
                }, { headers });
                toast.success('Comptage ajusté');
            }
            onSave();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Erreur');
        } finally {
            setSaving(false);
        }
    };

    const productName = count.product?.name || count.product_name || '—';
    const variantName = count.variant?.name || count.variant_name;

    return (
        <Modal
            title={isValidate ? 'Valider le comptage' : 'Ajuster le comptage'}
            subtitle={`${productName}${variantName ? ` — ${variantName}` : ''}`}
            onClose={onClose}
            size="sm"
            footer={<ModalFooter onCancel={onClose} onConfirm={handleSave} saving={saving} label={isValidate ? 'Valider' : 'Ajuster'}
                                 tone={isValidate ? 'text-green-600 dark:text-green-400' : 'text-purple-600 dark:text-purple-400'}/>}
        >
            <div className="space-y-4">
                {/* Current values */}
                <div className="grid grid-cols-2 border border-gray-200 dark:border-gray-700 rounded-lg divide-x divide-gray-200 dark:divide-gray-700">
                    <div className="px-4 py-3">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Stock attendu</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{count.expected_qty ?? '—'}</p>
                    </div>
                    <div className="px-4 py-3">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Compté par employé</p>
                        <p className="text-xl font-bold text-blue-700 dark:text-blue-400">{count.counted_qty ?? '—'}</p>
                    </div>
                </div>

                {/* Adjusted qty — only for adjust mode */}
                {!isValidate && (
                    <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                            Quantité corrigée <span className="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={form.adjusted_qty}
                            onChange={e => setForm(p => ({ ...p, adjusted_qty: e.target.value }))}
                            className={fieldClass}
                        />
                    </div>
                )}

                {/* Admin note */}
                <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                        Note admin <span className="text-gray-400 dark:text-gray-500 font-normal">(optionnel)</span>
                    </label>
                    <input
                        type="text"
                        value={form.admin_note}
                        onChange={e => setForm(p => ({ ...p, admin_note: e.target.value }))}
                        placeholder={isValidate ? 'Vérifié sur place, quantité confirmée' : 'Ex: 1 unité abîmée non comptabilisée'}
                        className={fieldClass}
                    />
                </div>
            </div>
        </Modal>
    );
};

// ─── CountsAdminView ──────────────────────────────────────────────────────────

const COUNT_FILTER_TABS = [
    { key: 'all',       label: 'Tous'       },
    { key: 'pending',   label: 'En attente' },
    { key: 'counted',   label: 'Comptés'    },
    { key: 'adjusted',  label: 'Ajustés'    },
    { key: 'validated', label: 'Validés'    },
];

const CountsAdminView = ({ session }) => {
    const [counts, setCounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [statusFilter, setStatusFilter] = useState('all');
    const [actionCount, setActionCount] = useState(null);
    const [actionMode, setActionMode] = useState('validate');
    const [refreshKey, setRefreshKey] = useState(0);
    const PER_PAGE = 20;

    useEffect(() => {
        let active = true;
        const fetchCounts = async () => {
            setLoading(true);
            try {
                const headers = await getAccountHeaders();
                let url = `/inventory/sessions/${session.id}/counts?page=${page}&perPage=${PER_PAGE}`;
                if (statusFilter !== 'all') url += `&status=${statusFilter}`;
                const res = await api.get(url, { headers });
                const data = res.data;
                if (active) {
                    setCounts(data?.data || data?.counts || (Array.isArray(data) ? data : []));
                    setTotalPages(data?.meta?.last_page || data?.totalPages || 1);
                }
            } catch {
                if (active) toast.error('Erreur lors du chargement des comptages');
            }
            if (active) setLoading(false);
        };
        fetchCounts();
        return () => { active = false; };
    }, [session.id, page, statusFilter, refreshKey]);

    const isEditable = !['validated', 'cancelled'].includes(session.status);

    return (
        <div>
            {/* Filter tabs */}
            <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
                {COUNT_FILTER_TABS.map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => { setStatusFilter(tab.key); setPage(1); }}
                        className={`flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-full border transition ${
                            statusFilter === tab.key
                                ? 'bg-primary text-white border-primary'
                                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-primary hover:text-primary'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <ListLoader/>
            ) : counts.length === 0 ? (
                <ListEmpty icon={Package} title="Aucun comptage trouvé"/>
            ) : (
                <>
                    <div className="space-y-3">
                        {counts.map(count => {
                            const gap = count.gap ?? ((count.counted_qty ?? null) !== null ? (count.counted_qty - count.expected_qty) : null);
                            const effectiveQty = count.adjusted_qty ?? count.counted_qty;
                            return (
                                <div key={count.id} className={mobileCardClass}>
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                                                {count.product?.name || count.product_name || '—'}
                                                {(count.variant?.name || count.variant_name) && (
                                                    <span className="text-xs text-gray-400 dark:text-gray-500 font-normal ml-1.5">
                                                        {count.variant?.name || count.variant_name}
                                                    </span>
                                                )}
                                            </p>
                                            <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                                <span>Attendu: <strong className="text-gray-700 dark:text-gray-300">{count.expected_qty}</strong></span>
                                                {effectiveQty !== null && effectiveQty !== undefined && (
                                                    <span>Compté: <strong className="text-gray-700 dark:text-gray-300">{effectiveQty}</strong></span>
                                                )}
                                                {gap !== null && <GapBadge gap={gap}/>}
                                            </div>
                                            {count.admin_note && (
                                                <p className="text-xs text-purple-600 dark:text-purple-400 mt-1 italic">Admin: {count.admin_note}</p>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1.5 flex-shrink-0">
                                            <StatusBadge status={count.status} config={COUNT_STATUS}/>
                                            {isEditable && ['counted', 'adjusted'].includes(count.status) && (
                                                <RowActions actions={[
                                                    { label: 'Valider', icon: CheckCircle, tone: 'green', onClick: () => { setActionCount(count); setActionMode('validate'); } },
                                                    ...(count.status === 'counted'
                                                        ? [{ label: 'Ajuster', icon: Edit2, onClick: () => { setActionCount(count); setActionMode('adjust'); } }]
                                                        : []),
                                                ]}/>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <Pagination page={page} totalPages={totalPages} onPage={setPage}/>
                </>
            )}

            {actionCount && (
                <CountAdjustModal
                    count={actionCount}
                    mode={actionMode}
                    onClose={() => setActionCount(null)}
                    onSave={() => { setActionCount(null); setRefreshKey(k => k + 1); }}
                />
            )}
        </div>
    );
};

// ─── SessionDetail ────────────────────────────────────────────────────────────

const SessionDetail = ({ session, onClose, onRefresh, onEdit }) => {
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('assignments');
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [editingAssignment, setEditingAssignment] = useState(null);
    const [showValidateModal, setShowValidateModal] = useState(false);
    const [showReport, setShowReport] = useState(false);

    const fetchAssignments = useCallback(async () => {
        setLoading(true);
        try {
            const headers = await getAccountHeaders();
            const res = await api.get(`/inventory/sessions/${session.id}/assignments`, { headers });
            setAssignments(res.data?.data || res.data || []);
        } catch { /* ignore */ }
        setLoading(false);
    }, [session.id]);

    useEffect(() => { fetchAssignments(); }, [fetchAssignments]);

    const handleStart = async () => {
        if (assignments.length === 0) {
            toast.error('Ajoutez au moins un assignment avant de démarrer');
            return;
        }
        setActionLoading(true);
        try {
            const headers = await getAccountHeaders();
            const res = await api.post(`/inventory/sessions/${session.id}/start`, {}, { headers });
            toast.success(`Session démarrée — ${res.data?.counts_generated || 0} comptages générés`);
            onRefresh();
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Erreur au démarrage');
        } finally {
            setActionLoading(false);
        }
    };

    const handleCancel = async () => {
        if (!window.confirm('Annuler cette session ? Cette action est irréversible.')) return;
        setActionLoading(true);
        try {
            const headers = await getAccountHeaders();
            await api.delete(`/inventory/sessions/${session.id}`, { headers });
            toast.success('Session annulée');
            onRefresh();
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Erreur');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteAssignment = async (id) => {
        if (!window.confirm('Supprimer cet assignment ?')) return;
        try {
            const headers = await getAccountHeaders();
            await api.delete(`/inventory/assignments/${id}`, { headers });
            toast.success('Assignment supprimé');
            fetchAssignments();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Erreur');
        }
    };

    const isDraft = session.status === 'draft';
    const isCompleted = session.status === 'completed';
    const isClosed = ['validated', 'cancelled'].includes(session.status);
    const showCounts = !isDraft;

    const DETAIL_TABS = [
        { key: 'assignments', label: 'Assignments', icon: Users },
        ...(showCounts ? [{ key: 'comptages', label: 'Comptages', icon: Package }] : []),
    ];

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-40 z-40" onClick={onClose}/>
            <div className="fixed top-0 right-0 h-full w-full max-w-xl bg-white dark:bg-gray-900 shadow-xl z-50 flex flex-col overflow-hidden">

                {/* Header */}
                <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                    <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                <StatusBadge status={session.status} config={SESSION_STATUS}/>
                                {isDraft && (
                                    <button
                                        onClick={() => onEdit(session)}
                                        className="p-1 text-gray-400 dark:text-gray-500 hover:text-primary hover:bg-primary/10 rounded-lg transition"
                                        title="Modifier la session"
                                    >
                                        <Edit2 className="w-3.5 h-3.5"/>
                                    </button>
                                )}
                            </div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">{session.name}</h2>
                            {session.note && <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{session.note}</p>}
                        </div>
                        <button onClick={onClose} aria-label="Fermer"
                                className="w-8 h-8 flex items-center justify-center rounded-lg ml-3 flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                            <X className="w-5 h-5"/>
                        </button>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
                        <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5"/>
                            Créée {moment(session.created_at).fromNow()}
                        </span>
                        {session.started_at && (
                            <span className="flex items-center gap-1">
                                <Play className="w-3.5 h-3.5"/>
                                Démarrée {moment(session.started_at).fromNow()}
                            </span>
                        )}
                        {session.validated_at && (
                            <span className="flex items-center gap-1">
                                <CheckCircle className="w-3.5 h-3.5 text-green-500 dark:text-green-400"/>
                                Validée {moment(session.validated_at).fromNow()}
                            </span>
                        )}
                    </div>
                </div>

                {/* Actions bar */}
                <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex gap-2 flex-wrap flex-shrink-0">
                    {isDraft && (
                        <button
                            onClick={handleStart}
                            disabled={actionLoading}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors disabled:opacity-60 text-primary border-primary hover:bg-primary hover:text-white"
                        >
                            {actionLoading ? <Loader className="w-4 h-4 animate-spin"/> : <Play className="w-4 h-4"/>}
                            Démarrer
                        </button>
                    )}
                    {isCompleted && (
                        <button
                            onClick={() => setShowValidateModal(true)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors disabled:opacity-60 text-green-700 dark:text-green-400 border-green-300 dark:border-green-800 hover:bg-green-50 dark:hover:bg-green-900/20"
                        >
                            <CheckCircle className="w-4 h-4"/>
                            Valider
                        </button>
                    )}
                    <button
                        onClick={() => setShowReport(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors disabled:opacity-60 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                        <BarChart2 className="w-4 h-4"/>
                        Rapport
                    </button>
                    {!isClosed && (
                        <button
                            onClick={handleCancel}
                            disabled={actionLoading}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors disabled:opacity-60 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 ml-auto"
                        >
                            <XCircle className="w-4 h-4"/>
                            Annuler
                        </button>
                    )}
                </div>

                {/* Tabs */}
                {DETAIL_TABS.length > 1 && (
                    <div className="flex gap-0 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 px-5">
                        {DETAIL_TABS.map(tab => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveTab(tab.key)}
                                    className={`${tabClass(activeTab === tab.key)} -mb-px`}
                                >
                                    <Icon className={tabIconClass}/>
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Tab content */}
                <div className="flex-1 overflow-y-auto p-5">

                    {/* ── Assignments tab ── */}
                    {activeTab === 'assignments' && (
                        <>
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider flex items-center gap-2">
                                    <Users className="w-4 h-4"/>
                                    {assignments.length} employé{assignments.length !== 1 ? 's' : ''} assigné{assignments.length !== 1 ? 's' : ''}
                                </h3>
                                {isDraft && (
                                    <Button variant="tinted" size="sm" icon={Plus}
                                            onClick={() => { setEditingAssignment(null); setShowAssignModal(true); }}>
                                        Ajouter
                                    </Button>
                                )}
                            </div>

                            {loading ? (
                                <ListLoader/>
                            ) : assignments.length === 0 ? (
                                <ListEmpty
                                    icon={Users}
                                    title="Aucun employé assigné"
                                    action={isDraft && (
                                        <button onClick={() => setShowAssignModal(true)}
                                                className="mt-4 flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary hover:text-white transition-colors">
                                            <Plus className="w-4 h-4"/> Assigner un employé
                                        </button>
                                    )}
                                />
                            ) : (
                                <div className="space-y-3">
                                    {assignments.map(asgn => {
                                        const ScopeIcon = SCOPE_LABELS[asgn.scope_type]?.icon || Layers;
                                        const canEdit = isDraft && !['submitted', 'validated'].includes(asgn.status);
                                        const progressDone = asgn.progress?.done ?? asgn.counts_done;
                                        const progressTotal = asgn.progress?.total ?? asgn.counts_total;
                                        const progressPct = progressTotal > 0 ? Math.round((progressDone / progressTotal) * 100) : 0;
                                        return (
                                            <div key={asgn.id} className={mobileCardClass}>
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="flex items-center gap-2.5">
                                                        <Avatar size="sm" name={`${asgn.employee?.firstName || asgn.employee?.first_name || asgn.employee?.name || ''}`}/>
                                                        <div>
                                                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                                                                {asgn.employee?.firstName || asgn.employee?.first_name || ''}{' '}
                                                                {asgn.employee?.lastName || asgn.employee?.last_name || asgn.employee?.name || ''}
                                                            </p>
                                                            <div className="flex items-center gap-1.5 mt-0.5">
                                                                <ScopeIcon className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500"/>
                                                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                    {SCOPE_LABELS[asgn.scope_type]?.label || asgn.scope_type}
                                                                    {asgn.scope_ids?.length > 0 && ` (${asgn.scope_ids.length})`}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-1.5">
                                                        <StatusBadge status={asgn.status} config={ASSIGNMENT_STATUS}/>
                                                        {canEdit && (
                                                            <RowActions actions={[
                                                                { label: 'Modifier', icon: Edit2, onClick: () => { setEditingAssignment(asgn); setShowAssignModal(true); } },
                                                                { label: 'Supprimer', icon: Trash2, tone: 'red', danger: true, onClick: () => handleDeleteAssignment(asgn.id) },
                                                            ]}/>
                                                        )}
                                                    </div>
                                                </div>
                                                {asgn.note && (
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 pl-11">{asgn.note}</p>
                                                )}
                                                {progressTotal > 0 && (
                                                    <div className="mt-2 pl-11">
                                                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                                                            <span>{progressDone}/{progressTotal} comptés</span>
                                                            <span>{progressPct}%</span>
                                                        </div>
                                                        <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5">
                                                            <div
                                                                className="h-1.5 rounded-full bg-primary transition-all"
                                                                style={{ width: `${progressPct}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}

                    {/* ── Comptages tab ── */}
                    {activeTab === 'comptages' && (
                        <CountsAdminView session={session}/>
                    )}
                </div>
            </div>

            {/* Sub-modals */}
            {showAssignModal && (
                <AssignmentModal
                    sessionId={session.id}
                    assignment={editingAssignment}
                    onClose={() => { setShowAssignModal(false); setEditingAssignment(null); }}
                    onSave={() => { setShowAssignModal(false); setEditingAssignment(null); fetchAssignments(); }}
                />
            )}
            {showValidateModal && (
                <ValidateModal
                    session={session}
                    onClose={() => setShowValidateModal(false)}
                    onValidated={() => { setShowValidateModal(false); onRefresh(); onClose(); }}
                />
            )}
            {showReport && (
                <ReportModal
                    session={session}
                    onClose={() => setShowReport(false)}
                />
            )}
        </>
    );
};

// ─── SessionCard (mobile) ──────────────────────────────────────────────────────

const sessionActions = (session, { onOpen, onEdit, onDelete }) => [
    { label: 'Ouvrir', icon: Eye, tone: 'green', onClick: onOpen },
    ...(session.status === 'draft' ? [{ label: 'Modifier', icon: Edit2, onClick: onEdit }] : []),
    ...(!['validated', 'cancelled'].includes(session.status)
        ? [{ label: 'Annuler la session', icon: XCircle, tone: 'red', danger: true, onClick: onDelete }]
        : []),
];

// Mobile row — same look as the catalogue mobile list
const SessionCard = ({ session, actions, onClick }) => (
    <div onClick={onClick} className={`${mobileRowClass} cursor-pointer`}>
        <div className="flex items-start gap-3 mb-3">
            <Avatar name={session.name} size="lg"/>
            <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">{session.name}</h3>
                {session.note && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 line-clamp-1 mb-2">{session.note}</p>
                )}
                <StatusBadge status={session.status} config={SESSION_STATUS}/>
            </div>
            <RowActions actions={actions}/>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
                <span className="text-gray-500 dark:text-gray-400 text-xs block mb-1">Employés</span>
                <div className="font-semibold text-gray-900 dark:text-gray-100">{session.assignments_count ?? session.assignments?.length ?? 0}</div>
            </div>
            <div>
                <span className="text-gray-500 dark:text-gray-400 text-xs block mb-1">Créée le</span>
                <div className="font-semibold text-gray-900 dark:text-gray-100">{moment(session.created_at).format('DD MMM YYYY')}</div>
            </div>
        </div>
    </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const STATUS_TABS = [
    { key: 'all', label: 'Toutes' },
    { key: 'draft', label: 'Brouillons' },
    { key: 'in_progress', label: 'En cours' },
    { key: 'completed', label: 'Terminées' },
    { key: 'validated', label: 'Validées' },
    { key: 'cancelled', label: 'Annulées' },
];

// createRequestKey: bumped by the Stock header "Nouveau" button to open the creation modal
const InventairePage = ({ createRequestKey = 0 }) => {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingSession, setEditingSession] = useState(null);
    const [selectedSession, setSelectedSession] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const PER_PAGE = 12;

    useEffect(() => {
        if (createRequestKey > 0) {
            setEditingSession(null);
            setShowCreateModal(true);
        }
    }, [createRequestKey]);

    useEffect(() => {
        let active = true;
        const fetchSessions = async () => {
            setLoading(true);
            try {
                const headers = await getAccountHeaders();
                let url = `/inventory/sessions?page=${page}&itemsPerPage=${PER_PAGE}`;
                if (statusFilter !== 'all') url += `&status=${statusFilter}`;
                const res = await api.get(url, { headers });
                const data = res.data;
                if (active) {
                    setSessions(data?.data || data?.sessions || (Array.isArray(data) ? data : []));
                    setTotalPages(data?.meta?.last_page || data?.totalPages || 1);
                }
            } catch {
                if (active) toast.error('Erreur lors du chargement des sessions');
            }
            if (active) setLoading(false);
        };
        fetchSessions();
        return () => { active = false; };
    }, [page, statusFilter, refreshKey]);

    const handleRefresh = () => setRefreshKey(k => k + 1);

    const handleQuickCancel = async (session) => {
        if (!window.confirm('Annuler cette session ? Cette action est irréversible.')) return;
        try {
            const headers = await getAccountHeaders();
            await api.delete(`/inventory/sessions/${session.id}`, { headers });
            toast.success('Session annulée');
            handleRefresh();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Erreur');
        }
    };

    const filtered = search.trim()
        ? sessions.filter(s => s.name?.toLowerCase().includes(search.toLowerCase()))
        : sessions;

    const openCreate = () => { setEditingSession(null); setShowCreateModal(true); };
    const actionsFor = (session) => sessionActions(session, {
        onOpen: () => setSelectedSession(session),
        onEdit: () => { setEditingSession(session); setShowCreateModal(true); },
        onDelete: () => handleQuickCancel(session),
    });
    const changeStatus = (status) => { setStatusFilter(status); setPage(1); };

    const chips = [
        search && { key: 'search', label: `Recherche: ${search}`, onRemove: () => setSearch('') },
        statusFilter !== 'all' && {
            key: 'status', label: `Statut: ${STATUS_TABS.find(t => t.key === statusFilter)?.label}`,
            className: 'bg-primary', onRemove: () => changeStatus('all'),
        },
    ].filter(Boolean);

    return (
        <div className="max-w-full">
            {/* Search + status — catalogue toolbar */}
            <div className="flex flex-col sm:flex-row">
                <SearchInput value={search} onChange={setSearch} placeholder="Rechercher une session..."/>
                {/* Statut + actualiser sur une même ligne (mobile compris) */}
                <div className="flex">
                    <ToolbarSelect label="Statut" value={statusFilter} onChange={changeStatus} className="flex-1 sm:w-52">
                        {STATUS_TABS.map(tab => <option key={tab.key} value={tab.key}>{tab.label === 'Toutes' ? 'Tous les statuts' : tab.label}</option>)}
                    </ToolbarSelect>
                    <button onClick={handleRefresh} title="Actualiser" aria-label="Actualiser"
                            className="flex items-center justify-center px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm transition-all">
                        <RefreshCw className="w-5 h-5 text-gray-600 dark:text-gray-300"/>
                    </button>
                </div>
            </div>
            <ActiveFilters chips={chips} onClearAll={() => { setSearch(''); changeStatus('all'); }}/>

            {loading ? (
                <ListLoader label="Chargement des sessions..."/>
            ) : (
                <ListCard>
                    {filtered.length === 0 ? (
                        <ListEmpty
                            icon={ClipboardList}
                            title="Aucune session trouvée"
                            text={statusFilter !== 'all' || search
                                ? 'Essayez un autre filtre.'
                                : "Créez votre première session d'inventaire — le comptage se fait sur l'app mobile."}
                            action={statusFilter === 'all' && !search && (
                                <Button variant="tinted" size="sm" icon={Plus} className="mt-4" onClick={openCreate}>
                                    Créer une session
                                </Button>
                            )}
                        />
                    ) : (<>
                        {/* Desktop table */}
                        <div className="hidden lg:block overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700/50">
                                <tr>
                                    <th className={`${thClass} w-2/5`}>Session</th>
                                    <th className={thClass}>Statut</th>
                                    <th className={thClass}>Employés</th>
                                    <th className={thClass}>Créée le</th>
                                    <th className={`${thClass} text-right`}>Actions</th>
                                </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {filtered.map(session => (
                                    <tr key={session.id} onClick={() => setSelectedSession(session)}
                                        className="transition-colors cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <Avatar name={session.name}/>
                                                <div className="min-w-0">
                                                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate max-w-sm">{session.name}</div>
                                                    {session.note && (
                                                        <div className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-sm mt-0.5">{session.note}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <StatusBadge status={session.status} config={SESSION_STATUS}/>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-xs font-medium">
                                                <Users className="w-3.5 h-3.5"/>
                                                {session.assignments_count ?? session.assignments?.length ?? 0}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                            {moment(session.created_at).format('DD MMM YYYY')}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <RowActions actions={actionsFor(session)}/>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile rows — catalogue style, edge to edge */}
                        <div className={mobileListClass}>
                            {filtered.map(session => (
                                <SessionCard
                                    key={session.id}
                                    session={session}
                                    actions={actionsFor(session)}
                                    onClick={() => setSelectedSession(session)}
                                />
                            ))}
                        </div>
                    </>)}
                </ListCard>
            )}

            {!loading && filtered.length > 0 && (
                <Pagination page={page} totalPages={totalPages} onPage={setPage}
                            summary={`${filtered.length} session${filtered.length > 1 ? 's' : ''} sur cette page`}/>
            )}

            {/* Modals */}
            {showCreateModal && (
                <CreateSessionModal
                    session={editingSession}
                    onClose={() => { setShowCreateModal(false); setEditingSession(null); }}
                    onSave={() => { setShowCreateModal(false); setEditingSession(null); handleRefresh(); }}
                />
            )}

            {selectedSession && (
                <SessionDetail
                    session={selectedSession}
                    onClose={() => setSelectedSession(null)}
                    onRefresh={() => { handleRefresh(); setSelectedSession(null); }}
                    onEdit={(session) => { setEditingSession(session); setShowCreateModal(true); }}
                />
            )}
        </div>
    );
};

export default InventairePage;
